import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Sheet,
    Typography,
    CircularProgress,
    Alert,
    Button,
    Grid,
    Stack,
    Radio,
    RadioGroup,
    Modal,
    ModalDialog,
    ModalClose,
    Divider,
    IconButton
} from '@mui/joy';
import FlagIcon from '@mui/icons-material/Flag';
import ClearIcon from '@mui/icons-material/Clear';
import domain from '../utils/domain';
import useExamRestrictions from '../hooks/useExamRestrictions';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Student, Exam, Registration, Question, Section } from '../types/exam';

const WriteExam: React.FC = () => {
    const { examId, registrationHash } = useParams<{ examId: string; registrationHash: string }>();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string>('');
    const [sections, setSections] = useState<Section[]>([]);
    const [currentSection, setCurrentSection] = useState<number>(0);
    const [currentQuestion, setCurrentQuestion] = useState<number>(0);
    const [answers, setAnswers] = useState<Record<number, number>>({});
    const [flaggedQuestions, setFlaggedQuestions] = useState<number[]>([]);
    const [timeRemaining, setTimeRemaining] = useState<number>(() => {
        const savedTime = localStorage.getItem('examTimeRemaining');
        if (savedTime) {
            return parseInt(savedTime);
        }
        return 0;
    });
    const [questionStartTime, setQuestionStartTime] = useState<number>(Date.now());
    const [tabChangeWarnings, setTabChangeWarnings] = useState<number>(0);
    const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
    const [showTimeUpModal, setShowTimeUpModal] = useState(false);
    const [timeUpCountdown, setTimeUpCountdown] = useState(5);
    const [maxDuration, setMaxDuration] = useState<number>(0);
    const [isOnline, setIsOnline] = useState(true);
    const [showOfflineModal, setShowOfflineModal] = useState(false);
    const [timerPaused, setTimerPaused] = useState(false);
    const lastOnlineTime = useRef<number>(Date.now());
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [videoPosition, setVideoPosition] = useState({ x: window.innerWidth - 224, y: window.innerHeight - 204 });
    const [isDragging, setIsDragging] = useState(false);
    const dragStartPos = useRef({ x: 0, y: 0 });
    const videoBoxRef = useRef<HTMLDivElement>(null);

    const navigate = useNavigate();
    const videoRef = React.useRef<HTMLVideoElement>(null);
    const [stream, setStream] = useState<MediaStream | null>(null);
    const [data, setData] = useState<{
        registration: Registration;
        student: Student;
        exam: Exam;
    } | null>(null);

    const initialViolationCount = parseInt(localStorage.getItem('violationCount') || '0');

    const { cleanup } = useExamRestrictions({
        onComplete: () => {
            console.log('Exam completed');
            submitExam();
        },
        onTabChange: () => {
            setTabChangeWarnings(prev => prev + 1);
            submitAnswer(sections[currentSection]?.questions[currentQuestion]?.id);

        },
        initialViolationCount
    });

    // Add session initialization on page reload
    useEffect(() => {
        const initSession = async () => {
            try {
                const start_time = new Date().toISOString();
                const response = await fetch(`${domain}/api/registrations/${examId}/${registrationHash}/start`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ start_time })
                });

                if (!response.ok) {
                    throw new Error('Failed to initialize session');
                }

                const result = await response.json();
                console.log("Session initialized:", result);

                // Update violation count
                const violation_count = result.violation_count || 0;
                localStorage.setItem('violationCount', violation_count.toString());
                setTabChangeWarnings(violation_count);

                // Update time remaining if exam already started
                if (result.start_time) {
                    const start_time_date = new Date(result.start_time);
                    const timeRemaining = (data?.exam?.duration || 0) * 60 - (new Date().getTime() - start_time_date.getTime());
                    localStorage.setItem('examTimeRemaining', timeRemaining.toString());
                    setTimeRemaining(timeRemaining);
                }
            } catch (err) {
                console.error('Error initializing session:', err);
                setError(err instanceof Error ? err.message : 'Failed to initialize session');
            }
        };

        // Check if this is a page reload or direct navigation
        const isPageReload = !sessionStorage.getItem('examSessionStarted');

        if (isPageReload) {
            // Initialize session only on page reload
            initSession();
            // Mark that we've started the session
            sessionStorage.setItem('examSessionStarted', 'true');
        }

    }, [examId, registrationHash, data?.exam?.duration]);

    // Define fetchExamDetails before its usage
    const fetchExamDetails = useCallback(async () => {
        try {
            const response = await fetch(`${domain}/api/registrations/${examId}/${registrationHash}`);
            if (!response.ok) {
                throw new Error('Failed to fetch exam details');
            }
            const data = await response.json();
            setData(data);

            // Only set initial time if it's not already set
            const savedTime = localStorage.getItem('examTimeRemaining');
            if (!savedTime) {
                const initialTime = data.exam.duration * 60;
                setTimeRemaining(initialTime);
                localStorage.setItem('examTimeRemaining', initialTime.toString());
            }

            setIsLoading(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load exam details');
            setIsLoading(false);
        }
    }, [examId, registrationHash]);

    // Define fetchQuestions before its usage
    const fetchQuestions = useCallback(async () => {
        try {
            const response = await fetch(`${domain}/api/registrations/${examId}/${registrationHash}/questions`);
            if (!response.ok) {
                throw new Error('Failed to fetch questions');
            }

            const data = await response.json();
            const questions = data.questions;

            // Group questions by section and sort by section_order
            const sectionMap = new Map<number, Section>();
            questions.forEach((question: Question) => {
                if (!sectionMap.has(question.section_id)) {
                    sectionMap.set(question.section_id, {
                        id: question.section_id,
                        name: question.section_name,
                        section_order: question.section_order,
                        questions: []
                    });
                }
                sectionMap.get(question.section_id)?.questions.push(question);
            });

            // Convert to array and sort by section_order
            const sectionsArray = Array.from(sectionMap.values())
                .sort((a, b) => (a.section_order || 0) - (b.section_order || 0))
                .map(section => ({
                    ...section,
                    questions: section.questions.sort(() => Math.random() - 0.5)
                }));

            setSections(sectionsArray);
            setIsLoading(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load questions');
            setIsLoading(false);
        }
    }, [examId, registrationHash]);

    // Request full screen function
    const requestFullScreen = async () => {
        try {
            const element = document.documentElement;
            if (element.requestFullscreen) {
                await element.requestFullscreen();
            } else if ((element as any).webkitRequestFullscreen) {
                await (element as any).webkitRequestFullscreen();
            } else if ((element as any).msRequestFullscreen) {
                await (element as any).msRequestFullscreen();
            }
        } catch (error) {
            console.warn('Failed to enter full screen:', error);
        }
    };

    // Add periodic full screen check
    useEffect(() => {
        const checkFullScreen = async () => {
            if (!document.fullscreenElement) {
                console.log('Screen is not in fullscreen mode, restoring...');
                await requestFullScreen();
            }
        };

        // Initial check
        checkFullScreen();

        // Set up periodic check every 5 seconds
        const intervalId = setInterval(checkFullScreen, 5000);

        // Cleanup interval on component unmount
        return () => {
            clearInterval(intervalId);
        };
    }, []);

    // Add full screen restoration on reload
    useEffect(() => {
        const restoreFullScreen = async () => {
            if (!document.fullscreenElement) {
                await requestFullScreen();
            }
        };
        restoreFullScreen();
    }, []);

    // Add video stream initialization
    useEffect(() => {
        if (data) {
            return;
        }
        const studentData = localStorage.getItem('studentData');
        console.log("Student Data", studentData);
        if (studentData) {
            const parsedData = JSON.parse(studentData);
            setData(parsedData);

            // Only set initial time if it's not already set
            const savedTime = localStorage.getItem('examTimeRemaining');
            if (!savedTime) {
                const initialTime = parsedData.exam.duration * 60;
                setTimeRemaining(initialTime);
                localStorage.setItem('examTimeRemaining', initialTime.toString());
            }
        } else {
            fetchExamDetails();
        }

        const initializeVideo = async () => {
            try {
                // Add a delay before initializing video
                await new Promise(resolve => setTimeout(resolve, 1000));

                const mediaStream = await navigator.mediaDevices.getUserMedia({
                    video: {
                        width: { ideal: 1280 },
                        height: { ideal: 720 },
                        facingMode: 'user'
                    },
                    audio: false
                });

                setStream(mediaStream);

                if (videoRef.current) {
                    videoRef.current.srcObject = mediaStream;
                    // Wait for metadata to load before playing
                    videoRef.current.onloadedmetadata = async () => {
                        try {
                            await videoRef.current?.play();
                            console.log('Video stream started successfully');
                        } catch (playError) {
                            console.error('Error playing video:', playError);
                            // Retry once if play fails
                            setTimeout(async () => {
                                try {
                                    await videoRef.current?.play();
                                    console.log('Video stream started on retry');
                                } catch (retryError) {
                                    console.error('Error playing video on retry:', retryError);
                                }
                            }, 1000);
                        }
                    };
                }
            } catch (err) {
                console.error('Error accessing camera:', err);
            }
        };

        // Add a small delay before first initialization
        setTimeout(() => {
            initializeVideo();
        }, 500);

        // Cleanup function
        return () => {
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }
            // Cleanup exam restrictions
            cleanup();
        };
    }, [fetchExamDetails, stream, cleanup]);

    useEffect(() => {
        fetchQuestions();
    }, [examId, registrationHash, fetchQuestions]);

    // Add effect to set answers from loaded questions
    useEffect(() => {
        if (sections.length > 0) {
            const existingAnswers: Record<number, number> = {};
            sections.forEach(section => {
                section.questions.forEach(question => {
                    if (question.selected_option) {
                        existingAnswers[question.id] = question.selected_option;
                    }
                });
            });
            setAnswers(existingAnswers);
        }
    }, [sections]);

    // Move submitAnswer before submitExam
    const submitAnswer = useCallback(async (questionId: number, forcedAnswer?: number) => {
        const timeTaken = Math.floor((Date.now() - questionStartTime) / 1000); // Time in seconds
        // Use forcedAnswer if provided, otherwise use the answer from state
        const answer = forcedAnswer !== undefined ? forcedAnswer : answers[questionId];

        // Find the current question data to get the student_answers.id
        const currentQuestionData = sections[currentSection]?.questions[currentQuestion];
        if (!currentQuestionData) {
            console.error('Current question data not found');
            return;
        }

        const submitData = {
            id: currentQuestionData.id, // This is the student_answers.id
            question_id: questionId,
            answer: answer || 0, // 0 if no answer selected or forcedAnswer is 0
            time_taken: timeTaken,
            tab_change_count: tabChangeWarnings,
            remaining_time: timeRemaining,
            end_date: new Date().toISOString(),
            violation_count: tabChangeWarnings,
            question_text: currentQuestionData.question_text || '',
            question_hash: currentQuestionData.question_hash || '',
            created_at: currentQuestionData.created_at || ''
        };
        console.log("Submit Data:", submitData);
        console.log("Answer:", answer);
        try {
            const response = await fetch(`${domain}/api/registrations/${examId}/${registrationHash}/answers`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(submitData),
            });

            if (!response.ok) {
                throw new Error('Failed to submit answer');
            }

        } catch (error) {
            console.error('Error submitting answer:', error);
            // Store failed submission in localStorage for retry
            const failedSubmissions = JSON.parse(localStorage.getItem('failedSubmissions') || '[]') as AnswerSubmission[];
            failedSubmissions.push(submitData);
            localStorage.setItem('failedSubmissions', JSON.stringify(failedSubmissions));
        }
    }, [questionStartTime, answers, examId, registrationHash, tabChangeWarnings, timeRemaining, sections, currentSection, currentQuestion]);

    // Move submitExam after submitAnswer
    const submitExam = useCallback(async () => {
        console.log("Submit Exam");
        submitAnswer(sections[currentSection]?.questions[currentQuestion]?.id);
        try {
            const submitData = {
                end_time: new Date().toISOString(),
                violation_count: tabChangeWarnings
            };
            const response = await fetch(`${domain}/api/registrations/${examId}/${registrationHash}/end`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(submitData),
            });

            if (!response.ok) {
                throw new Error('Failed to submit answer');
            }
            // Cleanup all stored data
            localStorage.removeItem('examTimeRemaining');
            localStorage.removeItem('violationCount');
            localStorage.removeItem('failedSubmissions');
            localStorage.removeItem('studentData');
            sessionStorage.removeItem('examSessionStarted');
            // Call cleanup function from useExamRestrictions
            cleanup();
            navigate('/exam/thankyou');
        } catch (error) {
            console.error('Error submitting answer:', error);
            // Still cleanup data even if submission fails
            localStorage.removeItem('examTimeRemaining');
            localStorage.removeItem('violationCount');
            localStorage.removeItem('failedSubmissions');
            localStorage.removeItem('studentData');
            sessionStorage.removeItem('examSessionStarted');
            // Call cleanup function from useExamRestrictions
            cleanup();
            navigate('/exam/thankyou');
        }
    }, [sections, currentSection, currentQuestion, tabChangeWarnings, examId, registrationHash, navigate, submitAnswer, cleanup]);

    // Initialize maxDuration when exam data is loaded
    useEffect(() => {
        if (data?.exam?.duration) {
            const durationInSeconds = data.exam.duration * 60;
            setMaxDuration(durationInSeconds);
        }
    }, [data]);

    // Add audio refs
    const twentySecAudioRef = useRef<HTMLAudioElement | null>(null);
    const beepAudioRef = useRef<HTMLAudioElement | null>(null);

    // Add audio initialization
    useEffect(() => {
        // Initialize audio elements
        twentySecAudioRef.current = new Audio(`${process.env.PUBLIC_URL}/audio/twenty-seconds.mp3`);
        beepAudioRef.current = new Audio(`${process.env.PUBLIC_URL}/audio/beep.mp3`);
    }, []);

    // Add internet connectivity monitoring
    useEffect(() => {
        const handleOnline = () => {
            setIsOnline(true);
            setShowOfflineModal(false);
            setTimerPaused(false);
            // Calculate time spent offline and adjust timer
            const timeOffline = Math.floor((Date.now() - lastOnlineTime.current) / 1000);
            setTimeRemaining(prev => prev + timeOffline);
        };

        const handleOffline = () => {
            setIsOnline(false);
            setShowOfflineModal(true);
            setTimerPaused(true);
            lastOnlineTime.current = Date.now();
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    // Modify the timer effect to respect pause state
    useEffect(() => {
        const timer = setInterval(() => {
            if (!timerPaused) {
                setTimeRemaining(prev => {
                    if (prev <= 0) {
                        clearInterval(timer);
                        setShowTimeUpModal(true);
                        return 0;
                    }

                    // Play 20-second warning
                    if (prev === 20) {
                        twentySecAudioRef.current?.play().catch(console.error);
                    }

                    // Play beep for last 10 seconds
                    if (prev <= 10 && prev > 0) {
                        beepAudioRef.current?.play().catch(console.error);
                    }

                    const newTime = prev - 1;
                    localStorage.setItem('examTimeRemaining', newTime.toString());
                    return newTime;
                });
            }
        }, 1000);

        return () => {
            clearInterval(timer);
        };
    }, [timerPaused]);

    // Add effect for time-up countdown and submission
    useEffect(() => {
        let countdownTimer: NodeJS.Timeout;
        if (showTimeUpModal) {
            countdownTimer = setInterval(() => {
                setTimeUpCountdown(prev => {
                    if (prev <= 1) {
                        clearInterval(countdownTimer);
                        submitExam();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => {
            if (countdownTimer) {
                clearInterval(countdownTimer);
            }
        };
    }, [showTimeUpModal, submitExam]);

    // Add submission type
    interface AnswerSubmission {
        question_id: number;
        answer: number;
        time_taken: number;
        tab_change_count: number;
        remaining_time: number;
        violation_count: number;
        question_hash: string;
        created_at: string;
    }

    // Function to handle question change
    const handleQuestionChange = useCallback(async (newSectionIndex: number, newQuestionIndex: number) => {
        // Submit current question's answer before changing
        const currentQuestionId = sections[currentSection]?.questions[currentQuestion]?.id;
        if (currentQuestionId) {
            console.log("currentQuestionId:", currentQuestionId);
            await submitAnswer(currentQuestionId);
        }

        // Update section and question
        setCurrentSection(newSectionIndex);
        setCurrentQuestion(newQuestionIndex);
        setQuestionStartTime(Date.now());
    }, [currentSection, currentQuestion, sections, submitAnswer]);

    // Modify the question box click handler
    const handleQuestionBoxClick = async (sectionIndex: number, questionIndex: number) => {
        await handleQuestionChange(sectionIndex, questionIndex);
    };

    // Modify the next button click handler
    const handleNextClick = async () => {
        if (currentQuestion < sections[currentSection]?.questions.length - 1) {
            // Next question in current section
            await handleQuestionChange(currentSection, currentQuestion + 1);
        } else if (currentSection < sections.length - 1) {
            // Move to first question of next section
            await handleQuestionChange(currentSection + 1, 0);
        }
    };

    // Modify the previous button click handler
    const handlePreviousClick = async () => {
        if (currentQuestion > 0) {
            // Previous question in current section
            await handleQuestionChange(currentSection, currentQuestion - 1);
        } else if (currentSection > 0) {
            // Move to last question of previous section
            await handleQuestionChange(
                currentSection - 1,
                sections[currentSection - 1].questions.length - 1
            );
        }
    };

    const handleAnswerSelect = async (questionId: number, value: number) => {
        // If value is 0 (Clear Answer button clicked)
        if (value === 0) {
            // First clear from local state
            const newAnswers = { ...answers };
            delete newAnswers[questionId];
            setAnswers(newAnswers);

            try {
                // Submit with forced answer value of 0
                await submitAnswer(questionId, 0);
            } catch (error) {
                console.error('Error submitting cleared answer:', error);
            }
        } else {
            // For normal answer selection (1,2,3,4)
            setAnswers(prev => ({
                ...prev,
                [questionId]: value
            }));
        }
    };

    const handleFlagQuestion = (questionId: number) => {
        setFlaggedQuestions(prev =>
            prev.includes(questionId)
                ? prev.filter(id => id !== questionId)
                : [...prev, questionId]
        );
    };

    const formatTime = (seconds: number): string => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = seconds % 60;
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    // Add retry failed submissions function
    const retryFailedSubmissions = useCallback(async () => {
        const failedSubmissions = JSON.parse(localStorage.getItem('failedSubmissions') || '[]') as AnswerSubmission[];
        if (failedSubmissions.length === 0) return;

        const successfulRetries: AnswerSubmission[] = [];

        console.log("failedSubmissions:", failedSubmissions);
        for (const submission of failedSubmissions) {
            try {
                console.log(" in Retry Failed submittion data submission:", JSON.stringify(submission));
                const response = await fetch(`${domain}/api/registrations/${examId}/${registrationHash}/answers`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(submission),
                });

                if (response.ok) {
                    successfulRetries.push(submission);
                }
            } catch (error) {
                console.error('Error retrying submission:', error);
            }
        }

        // Remove successful retries from localStorage
        const remainingSubmissions = failedSubmissions.filter(
            submission => !successfulRetries.some(
                success => success.question_id === submission.question_id
            )
        );
        localStorage.setItem('failedSubmissions', JSON.stringify(remainingSubmissions));
    }, [examId, registrationHash]);

    // Add effect to retry failed submissions when page is restored
    useEffect(() => {
        if (!isLoading && data) {
            retryFailedSubmissions();
        }
    }, [isLoading, data, retryFailedSubmissions]);

    // Modify the submit exam handler
    const handleSubmitClick = () => {
        setShowSubmitConfirm(true);
    };

    const handleConfirmSubmit = async () => {
        setShowSubmitConfirm(false);
        await submitExam();
    };

    // Handle drag start for both mouse and touch
    const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
        setIsDragging(true);
        const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
        dragStartPos.current = {
            x: clientX - videoPosition.x,
            y: clientY - videoPosition.y
        };
    };

    // Handle drag move for both mouse and touch
    const handleDragMove = useCallback((e: MouseEvent | TouchEvent) => {
        if (isDragging && videoBoxRef.current) {
            const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
            const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

            const newX = clientX - dragStartPos.current.x;
            const newY = clientY - dragStartPos.current.y;

            // Calculate boundaries
            const maxX = window.innerWidth - videoBoxRef.current.offsetWidth;
            const maxY = window.innerHeight - videoBoxRef.current.offsetHeight;

            // Ensure the video stays within viewport bounds
            const boundedX = Math.max(0, Math.min(newX, maxX));
            const boundedY = Math.max(0, Math.min(newY, maxY));

            setVideoPosition({ x: boundedX, y: boundedY });

            // Prevent default scrolling on mobile while dragging
            if ('touches' in e) {
                e.preventDefault();
            }
        }
    }, [isDragging]);

    // Handle drag end for both mouse and touch
    const handleDragEnd = useCallback(() => {
        setIsDragging(false);
    }, []);

    // Add and remove event listeners for both mouse and touch
    useEffect(() => {
        if (isDragging) {
            // Mouse events
            window.addEventListener('mousemove', handleDragMove);
            window.addEventListener('mouseup', handleDragEnd);

            // Touch events
            window.addEventListener('touchmove', handleDragMove, { passive: false });
            window.addEventListener('touchend', handleDragEnd);
            window.addEventListener('touchcancel', handleDragEnd);
        }
        return () => {
            // Cleanup mouse events
            window.removeEventListener('mousemove', handleDragMove);
            window.removeEventListener('mouseup', handleDragEnd);

            // Cleanup touch events
            window.removeEventListener('touchmove', handleDragMove);
            window.removeEventListener('touchend', handleDragEnd);
            window.removeEventListener('touchcancel', handleDragEnd);
        };
    }, [isDragging, handleDragMove, handleDragEnd]);

    // Update position on window resize
    useEffect(() => {
        const handleResize = () => {
            if (videoBoxRef.current) {
                const maxX = window.innerWidth - videoBoxRef.current.offsetWidth;
                const maxY = window.innerHeight - videoBoxRef.current.offsetHeight;
                setVideoPosition(prev => ({
                    x: Math.min(prev.x, maxX),
                    y: Math.min(prev.y, maxY)
                }));
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <CircularProgress size="lg" />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <Alert color="danger" variant="soft">
                    {error}
                </Alert>
            </Box>
        );
    }

    const currentSectionData = sections[currentSection];
    const currentQuestionData = currentSectionData?.questions[currentQuestion];

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            {/* Add audio elements */}
            <audio id="twentySecAudio" src={`${process.env.PUBLIC_URL}/audio/twenty-seconds.mp3`} preload="auto" />
            <audio id="beepAudio" src={`${process.env.PUBLIC_URL}/audio/beep.mp3`} preload="auto" />

            {/* Header */}
            <Sheet
                variant="outlined"
                sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1px solid',
                    borderColor: '#ea7a23',
                    bgcolor: 'white'
                }}
            >
                {/* Left - Logo and Title */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flex: 1 }}>
                    <Box sx={{ width: '200px', height: 'auto' }}>
                        <img
                            src={`${process.env.PUBLIC_URL}/logo-vector.svg`}
                            alt="Vector India Logo"
                            style={{ width: '100%', height: 'auto' }}
                        />
                    </Box>
                    <Box>
                        <Typography
                            level="h3"
                            sx={{
                                color: 'text.primary',
                                fontSize: '1.5rem',
                                fontWeight: 600,
                                mb: 0.5
                            }}
                        >
                            {data?.exam?.title}
                        </Typography>
                        <Typography
                            level="body-md"
                            sx={{
                                color: 'text.secondary',
                                fontSize: '1rem'
                            }}
                        >
                            Name: {data?.student?.name}
                        </Typography>
                    </Box>
                </Box>

                {/* Center - Timer */}
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    flex: 1
                }}>
                    <Typography
                        level="body-lg"
                        sx={{
                            color: '#ea7a23',
                            fontSize: '1.2rem',
                            fontWeight: 500,
                            textAlign: 'center'
                        }}
                    >
                        Time Remaining
                    </Typography>
                    <Typography
                        level="h4"
                        sx={{
                            fontFamily: 'monospace',
                            color: timeRemaining <= 300 ? '#ff0000' : '#ea7a23',
                            fontSize: '2.5rem',
                            fontWeight: 600,
                            lineHeight: 1.2
                        }}
                    >
                        {formatTime(timeRemaining)}
                    </Typography>
                </Box>

                {/* Right - Submit Button */}
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="solid"
                        onClick={handleSubmitClick}
                        sx={{
                            backgroundColor: '#4CAF50',
                            color: 'white',
                            '&:hover': {
                                backgroundColor: '#388E3C'
                            },
                            borderRadius: '8px',
                            fontSize: '1rem',
                            py: 1,
                            px: 3,
                            fontWeight: 600
                        }}
                    >
                        Submit Exam
                    </Button>
                </Box>
            </Sheet>

            {/* Main Content */}
            <Box sx={{ display: 'flex', flex: 1, position: 'relative' }}>
                {/* Left Sidebar with Toggle */}
                <Box sx={{
                    position: 'relative',
                    width: isSidebarOpen ? '250px' : '0px',
                    transition: 'width 0.3s ease',
                    flexShrink: 0
                }}>
                    {/* Sidebar Content */}
                    <Sheet
                        variant="outlined"
                        sx={{
                            width: '250px',
                            height: '100%',
                            borderRight: '1px solid',
                            borderColor: '#ea7a23',
                            bgcolor: 'white',
                            transform: isSidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
                            transition: 'transform 0.3s ease',
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative'
                        }}
                    >
                        {/* Toggle Button */}
                        <Box
                            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                            sx={{
                                position: 'absolute',
                                right: '-32px',
                                top: '10px',
                                width: '32px',
                                height: '32px',
                                backgroundColor: '#ea7a23',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                borderRadius: '0 4px 4px 0',
                                color: 'white',
                                '&:hover': {
                                    backgroundColor: '#d86a1f'
                                },
                                zIndex: 1000,
                                boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                            }}
                        >
                            {isSidebarOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                        </Box>

                        {/* Sections Container */}
                        <Box sx={{
                            flex: 1,
                            overflowY: 'auto',
                            p: 2
                        }}>
                            {sections.map((section, sectionIndex) => (
                                <Box key={section.id} sx={{ mb: 3 }}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        color="neutral"
                                        onClick={() => handleQuestionChange(sectionIndex, 0)}
                                        sx={{
                                            mb: 2,
                                            borderColor: '#ea7a23',
                                            color: '#ea7a23',
                                            '&:hover': {
                                                borderColor: '#d86a1f',
                                                backgroundColor: 'rgba(234, 122, 35, 0.08)'
                                            },
                                            textAlign: 'center',
                                            borderRadius: '8px',
                                            fontSize: '0.9rem',
                                            py: 1
                                        }}
                                    >
                                        {section.name}
                                    </Button>
                                    <Grid
                                        container
                                        spacing={1}
                                        sx={{
                                            '& .MuiGrid-item': {
                                                pb: 0.5,
                                                pr: 0.5
                                            }
                                        }}
                                    >
                                        {section.questions.map((question, questionIndex) => {
                                            const isCurrentQuestion = currentSection === sectionIndex && currentQuestion === questionIndex;
                                            const isAnswered = answers[question.id] !== undefined && answers[question.id] !== 0;
                                            const isFlagged = flaggedQuestions.includes(question.id);

                                            return (
                                                <Grid xs={2} key={question.id}>
                                                    <Button
                                                        variant="outlined"
                                                        onClick={() => handleQuestionBoxClick(sectionIndex, questionIndex)}
                                                        sx={{
                                                            width: '28px',
                                                            height: '28px',
                                                            minWidth: 'unset',
                                                            p: 0,
                                                            borderRadius: '4px',
                                                            position: 'relative',
                                                            fontSize: '0.75rem',
                                                            border: isCurrentQuestion ? '2px solid #ea7a23' : '1px solid #4a4a4a',
                                                            color: isCurrentQuestion ? '#ea7a23' : '#4a4a4a',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            ...(isCurrentQuestion && {
                                                                bgcolor: '#fff4eb',
                                                                border: '2px solid #ea7a23',
                                                                color: '#ea7a23',
                                                                '&:hover': {
                                                                    bgcolor: '#fff4eb',
                                                                    border: '2px solid #ea7a23'
                                                                }
                                                            }),
                                                            ...(isAnswered && !isCurrentQuestion && {
                                                                bgcolor: 'rgba(76, 175, 80, 0.1)',
                                                                borderColor: '#4CAF50',
                                                                color: '#4CAF50'
                                                            }),
                                                            '&:hover': {
                                                                border: isCurrentQuestion ? '2px solid #ea7a23' : '1px solid #4a4a4a',
                                                                color: isCurrentQuestion ? '#ea7a23' : '#4a4a4a',
                                                                bgcolor: isCurrentQuestion ? '#fff4eb' : 'transparent'
                                                            }
                                                        }}
                                                    >
                                                        {questionIndex + 1}
                                                        {isFlagged && (
                                                            <FlagIcon
                                                                sx={{
                                                                    position: 'absolute',
                                                                    top: -8,
                                                                    right: -8,
                                                                    fontSize: '1rem',
                                                                    color: 'warning.500',
                                                                    filter: 'drop-shadow(0px 0px 1px white)'
                                                                }}
                                                            />
                                                        )}
                                                    </Button>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </Box>
                            ))}
                        </Box>
                    </Sheet>
                </Box>

                {/* Main Question Area */}
                <Box sx={{
                    flex: 1,
                    p: 3,
                    pl: 5,
                    overflowY: 'auto',
                    pb: '400px'
                }}>
                    {currentQuestionData && (
                        <Stack spacing={3}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box>
                                    <Typography level="h4">
                                        {currentSectionData.name} - Question {currentQuestion + 1}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                        <Button
                                            variant="outlined"
                                            color="neutral"
                                            onClick={handlePreviousClick}
                                            startDecorator={<NavigateBeforeIcon sx={{ color: currentSection === 0 && currentQuestion === 0 ? 'neutral.500' : '#ea7a23' }} />}
                                            size="sm"
                                            disabled={currentSection === 0 && currentQuestion === 0}
                                            sx={{
                                                borderColor: currentSection === 0 && currentQuestion === 0 ? 'neutral.500' : '#ea7a23',
                                                color: currentSection === 0 && currentQuestion === 0 ? 'neutral.500' : '#ea7a23',
                                                '&:hover': {
                                                    borderColor: '#d86a1f',
                                                    backgroundColor: 'rgba(234, 122, 35, 0.08)'
                                                }
                                            }}
                                        >
                                            Previous
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            onClick={handleNextClick}
                                            endDecorator={<NavigateNextIcon sx={{
                                                color: (currentSection === sections.length - 1 &&
                                                    currentQuestion === currentSectionData.questions.length - 1)
                                                    ? 'neutral.500' : '#ea7a23'
                                            }} />}
                                            size="sm"
                                            disabled={currentSection === sections.length - 1 &&
                                                currentQuestion === currentSectionData.questions.length - 1}
                                            sx={{
                                                borderColor: (currentSection === sections.length - 1 &&
                                                    currentQuestion === currentSectionData.questions.length - 1)
                                                    ? 'neutral.500' : '#ea7a23',
                                                color: (currentSection === sections.length - 1 &&
                                                    currentQuestion === currentSectionData.questions.length - 1)
                                                    ? 'neutral.500' : '#ea7a23',
                                                '&:hover': {
                                                    borderColor: '#d86a1f',
                                                    backgroundColor: 'rgba(234, 122, 35, 0.08)'
                                                }
                                            }}
                                        >
                                            Next
                                        </Button>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        variant="outlined"
                                        color="neutral"
                                        onClick={() => handleAnswerSelect(currentQuestionData.id, 0)}
                                        disabled={!answers[currentQuestionData.id]}
                                        startDecorator={<ClearIcon sx={{ color: answers[currentQuestionData.id] ? '#ea7a23' : 'inherit' }} />}
                                        sx={{
                                            borderColor: '#ea7a23',
                                            color: '#ea7a23',
                                            '&:hover': {
                                                borderColor: '#d86a1f',
                                                backgroundColor: 'rgba(234, 122, 35, 0.08)'
                                            }
                                        }}
                                    >
                                        Clear Answer
                                    </Button>
                                    <Button
                                        variant={flaggedQuestions.includes(currentQuestionData.id) ? 'solid' : 'outlined'}
                                        color="warning"
                                        onClick={() => handleFlagQuestion(currentQuestionData.id)}
                                        startDecorator={<FlagIcon />}
                                        sx={{
                                            borderColor: '#ea7a23',
                                            color: flaggedQuestions.includes(currentQuestionData.id) ? 'white' : '#ea7a23',
                                            backgroundColor: flaggedQuestions.includes(currentQuestionData.id) ? '#ea7a23' : 'transparent',
                                            '&:hover': {
                                                borderColor: '#d86a1f',
                                                backgroundColor: flaggedQuestions.includes(currentQuestionData.id) ? '#d86a1f' : 'rgba(234, 122, 35, 0.08)'
                                            }
                                        }}
                                    >
                                        {flaggedQuestions.includes(currentQuestionData.id) ? 'Flagged' : 'Flag for Review'}
                                    </Button>
                                </Box>
                            </Box>

                            <Box
                                dangerouslySetInnerHTML={{ __html: currentQuestionData.question_text }}
                                sx={{
                                    '& code': {
                                        backgroundColor: 'background.level2',
                                        p: 0.5,
                                        borderRadius: 'sm',
                                        fontFamily: 'monospace'
                                    },
                                    userSelect: 'none',
                                    WebkitUserSelect: 'none',
                                    MozUserSelect: 'none',
                                    msUserSelect: 'none'
                                }}
                            />

                            {currentQuestionData.image_url && (
                                <Box sx={{ my: 2 }}>
                                    <img
                                        src={`${process.env.PUBLIC_URL}/data/${currentQuestionData.image_url}`}
                                        alt="Question"
                                        style={{ maxWidth: '100%', height: 'auto' }}
                                    />
                                </Box>
                            )}

                            <RadioGroup
                                value={answers[currentQuestionData.id]?.toString() || ''}
                                onChange={(e) => handleAnswerSelect(currentQuestionData.id, parseInt(e.target.value))}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0,
                                    width: '100%',
                                    alignItems: 'stretch',
                                    '--Radio-action-radius': '8px',
                                    '--Radio-gap': '0px',
                                    '& .MuiRadio-radio': {
                                        borderColor: '#ea7a23',
                                        '&.Mui-checked': {
                                            borderColor: '#ea7a23',
                                            backgroundColor: 'transparent',
                                            '&:before': {
                                                backgroundColor: '#000000'
                                            }
                                        }
                                    }
                                }}
                            >
                                <Stack spacing={1}>
                                    {[
                                        { value: 1, text: currentQuestionData.option_a },
                                        { value: 2, text: currentQuestionData.option_b },
                                        ...(currentQuestionData.option_c ? [{ value: 3, text: currentQuestionData.option_c }] : []),
                                        ...(currentQuestionData.option_d ? [{ value: 4, text: currentQuestionData.option_d }] : [])
                                    ].map(option => (
                                        <Sheet
                                            key={option.value}
                                            variant="outlined"
                                            sx={{
                                                p: 1,
                                                borderRadius: 'sm',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                display: 'flex',
                                                alignItems: 'center',
                                                minHeight: '30px',
                                                borderColor: answers[currentQuestionData.id] === option.value ? '#ea7a23' : 'divider',
                                                bgcolor: answers[currentQuestionData.id] === option.value ? 'rgba(234, 122, 35, 0.08)' : 'transparent',
                                                '&:hover': {
                                                    bgcolor: 'rgba(234, 122, 35, 0.08)',
                                                    borderColor: '#ea7a23'
                                                }
                                            }}
                                            onClick={() => handleAnswerSelect(currentQuestionData.id, option.value)}
                                        >
                                            <Radio
                                                checked={answers[currentQuestionData.id] === option.value}
                                                value={option.value.toString()}
                                                label={
                                                    <Box
                                                        dangerouslySetInnerHTML={{ __html: option.text }}
                                                        sx={{
                                                            flex: 1,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            '& code': {
                                                                backgroundColor: 'background.level2',
                                                                p: 0.5,
                                                                borderRadius: 'sm',
                                                                fontFamily: 'monospace'
                                                            }
                                                        }}
                                                    />
                                                }
                                                sx={{
                                                    width: '100%',
                                                    p: 0,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    '& .MuiRadio-action': {
                                                        display: 'none'
                                                    },
                                                    '& .MuiRadio-radio': {
                                                        mr: 1.5,
                                                        alignSelf: 'center'
                                                    },
                                                    '& .MuiRadio-label': {
                                                        flex: 1,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        minHeight: 'unset',
                                                        py: 0.25
                                                    }
                                                }}
                                            />
                                        </Sheet>
                                    ))}
                                </Stack>
                            </RadioGroup>
                        </Stack>
                    )}
                </Box>

                {/* Fixed Video Feed */}
                <Box
                    ref={videoBoxRef}
                    onMouseDown={handleDragStart}
                    onTouchStart={handleDragStart}
                    sx={{
                        position: 'fixed',
                        left: `${videoPosition.x}px`,
                        top: `${videoPosition.y}px`,
                        width: '204px',
                        height: '153px',
                        zIndex: 9999,
                        backgroundColor: 'black',
                        borderRadius: '8px',
                        border: '2px solid #ea7a23',
                        overflow: 'hidden',
                        cursor: isDragging ? 'grabbing' : 'grab',
                        touchAction: 'none', // Prevent default touch actions
                        '& video': {
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transform: 'scaleX(-1)'
                        },
                        transition: isDragging ? 'none' : 'all 0.2s ease',
                        '&:hover': {
                            boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                        }
                    }}
                >
                    <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                    />
                </Box>
            </Box>

            {/* Add Time Up Modal */}
            <Modal
                open={showTimeUpModal}
                onClose={() => { }}  // Empty onClose to prevent closing
            >
                <ModalDialog
                    variant="outlined"
                    sx={{
                        maxWidth: 500,
                        borderRadius: 'md',
                        p: 3,
                        boxShadow: 'lg',
                    }}
                >
                    <Typography
                        component="h2"
                        level="h4"
                        textColor="danger"
                        fontWeight="lg"
                        mb={1}
                    >
                        Time's Up!
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <Typography level="body-md" mb={2}>
                        Your exam time has ended. Your answers will be automatically submitted in:
                    </Typography>
                    <Typography
                        level="h2"
                        sx={{
                            textAlign: 'center',
                            color: 'danger.500',
                            fontFamily: 'monospace',
                            fontSize: '3rem',
                            mb: 2
                        }}
                    >
                        {timeUpCountdown}
                    </Typography>
                    <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                        Please wait while we submit your exam...
                    </Typography>
                </ModalDialog>
            </Modal>

            {/* Add Submit Confirmation Modal */}
            <Modal
                open={showSubmitConfirm}
                onClose={() => setShowSubmitConfirm(false)}
            >
                <ModalDialog
                    variant="outlined"
                    sx={{
                        maxWidth: 500,
                        borderRadius: 'md',
                        p: 3,
                        boxShadow: 'lg',
                    }}
                >
                    <ModalClose />
                    <Typography
                        component="h2"
                        level="h4"
                        textColor="danger"
                        fontWeight="lg"
                        mb={1}
                    >
                        Confirm Exam Submission
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <Typography level="body-md" mb={2}>
                        Are you sure you want to submit your exam? This action cannot be undone.
                        Please ensure you have:
                    </Typography>
                    <Box sx={{ ml: 2, mb: 2 }}>
                        <Typography level="body-md" mb={1}>
                            • Answered all questions you know
                        </Typography>
                        <Typography level="body-md" mb={1}>
                            • Reviewed flagged questions
                        </Typography>
                        <Typography level="body-md" mb={1}>
                            • Double-checked your answers
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 3 }}>
                        <Button
                            variant="plain"
                            color="neutral"
                            onClick={() => setShowSubmitConfirm(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="solid"
                            color="danger"
                            onClick={handleConfirmSubmit}
                        >
                            Submit Exam
                        </Button>
                    </Box>
                </ModalDialog>
            </Modal>

            {/* Add Offline Modal */}
            <Modal
                open={showOfflineModal}
                onClose={() => { }}  // Empty onClose to prevent closing
            >
                <ModalDialog
                    variant="outlined"
                    sx={{
                        maxWidth: 500,
                        borderRadius: 'md',
                        p: 3,
                        boxShadow: 'lg',
                    }}
                >
                    <Typography
                        component="h2"
                        level="h4"
                        textColor="danger"
                        fontWeight="lg"
                        mb={1}
                    >
                        No Internet Connection
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <Typography level="body-md" mb={2}>
                        Your internet connection appears to be offline. The exam timer has been paused.
                    </Typography>
                    <Typography level="body-md" mb={2}>
                        Please check your connection. The exam will resume automatically when your connection is restored.
                    </Typography>
                    <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                        Don't worry, your progress is saved and time will be adjusted accordingly.
                    </Typography>
                </ModalDialog>
            </Modal>
        </Box>
    );
};

export default WriteExam; 